import { FC, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';

import { userActions } from '@/redux/actions';
import { useAppDispatch } from '@/redux/hooks';
import { UpdateLocaleRequest } from '@/api';

import { useOnClickOutside } from '@/hooks';
import { DropdownPanel } from '@/components';
import { localeLabels, locales } from '@/i18n';
import { noop } from '@/helpers';

import styles from './LangSwitcher.module.sass';

interface LangSwitcherProps {
  isActive?: boolean;
  isShowArrow?: boolean;
  isRounded?: boolean;
  fullLabel?: boolean;
  className?: string;
}

const LangSwitcher: FC<LangSwitcherProps> = ({
  isActive = false,
  isShowArrow = true,
  isRounded = false,
  fullLabel,
  className,
}) => {
  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(isActive);
  const langRef = useRef<HTMLDivElement>(null);
  const currentLang = i18n.languages[0];

  const formattedLang = (lang: string): string => {
    if (fullLabel) {
      return localeLabels[lang as keyof typeof localeLabels];
    }

    return lang.toLocaleUpperCase();
  };

  const handleOnOpen = () => {
    setIsOpen((prev) => !prev);
  };

  const handleOnClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleChangeLangTo = useCallback(
    (nextLocale: string) => {
      i18n.changeLanguage(nextLocale);
      localStorage.setItem('lng', nextLocale);
      const payload: UpdateLocaleRequest = {
        locale: nextLocale,
      };
      dispatch(userActions.updateLocale(payload));
      handleOnClose();
    },
    [handleOnClose, i18n],
  );

  useOnClickOutside([langRef], handleOnClose, undefined, []);

  const classes = cx(
    styles.lang,
    {
      [styles.full]: fullLabel,
      [styles['is-active']]: isOpen,
      [styles['is-show-arrow']]: isShowArrow,
      [styles['is-rounded']]: isRounded,
    },
    className,
  );

  if (!locales) return null;

  return (
    <nav className={classes} ref={langRef}>
      <button type="button" className={styles.current} onClick={handleOnOpen}>
        {formattedLang(currentLang)}
      </button>

      <DropdownPanel isActive={isOpen} className={styles.list} onClose={noop}>
        {locales.map((lang) => {
          if (lang === currentLang) return null;

          return (
            <button
              type="button"
              key={lang}
              className={styles.item}
              onClick={() => handleChangeLangTo(lang)}
            >
              {formattedLang(lang)}
            </button>
          );
        })}
      </DropdownPanel>
    </nav>
  );
};

export default LangSwitcher;
